import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import {RouteComponentProps, withRouter} from 'react-router-dom';

import _get from 'lodash/get';
import {DataExportModel} from '../../types/DataExport';
import {ContainerWithListAndForm,
  DataExportCreating,
  DataExportDetails,
  DataExportTable} from '../../components';

import {
  COMPRESSED_MODE_TABLE as COMPRESSED,
  FULL_WIDTH_MODE_TABLE as FULL_WIDTH,
  MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM,
  STATES_ENTITY,
  ROUTES,
} from '../../constants';
import {changeEditModeAndDataExportState,
  DATA_EXPORT_REDUCER} from '../../containers/DataExportEditForm/slice';
import {createDataExport, deleteDataExport, editDataExport, getDataExports} from './action';
import {OperationStatus} from '../../components/LastOperationStatusIndicator/constants';

type DataExportProps = RouteComponentProps;

function DataExport({history}: DataExportProps) {
  const dispatch = useDispatch();
  const accountInfo = useSelector((store: any) => store.operatorLayoutReducer.accountInfo);
  const [containerMode, setContainerMode]
    = useState(MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_FULL_SCREEN);
  const [tableMode, setTableMode] = useState(FULL_WIDTH);
  const isEditMode = useSelector((store: any) => store[DATA_EXPORT_REDUCER].isEditMode);
  const dataExports: DataExportModel[]
    = useSelector((store: any) => store[DATA_EXPORT_REDUCER].dataExports);
  const [selectedDataExportId, setSelectedDataExportId] = useState<string | null>(null);
  const [selectedDataExport, setSelectedDataExport] = useState<DataExportModel | null>(null);
  const [originalDataExport, setOriginalDataExport] = useState<DataExportModel | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (_get(accountInfo, 'logStreamingAllowed', false)) {
      (getDataExports())(dispatch);
    } else {
      history.push(ROUTES.DEFAULT);
    }
  }, [accountInfo, dispatch, history]);

  let timer: ReturnType<typeof setInterval>;

  useEffect(() => {
    if (containerMode === MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_FULL_SCREEN &&
        !timer &&
        dataExports.findIndex((d) => d.status === OperationStatus.IN_PROGRESS) > -1) {
      timer = setInterval(() => {
        (getDataExports())(dispatch);
      }, 5000);
    } else if (timer) {
      clearInterval(timer);
    }

    return () => clearInterval(timer);
  }, [containerMode, dataExports, dispatch]);

  const handleGoBack = () => {
    setContainerMode(MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_FULL_SCREEN);
    setTableMode(FULL_WIDTH);
    setSelectedIndex(-1);
    dispatch(changeEditModeAndDataExportState({
      isEditMode: false,
      state: STATES_ENTITY.NONE,
    }));
  };

  const handleCreateNew = (formData: DataExportModel) => {
    setContainerMode(MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_FULL_SCREEN);
    setTableMode(FULL_WIDTH);
    dispatch(changeEditModeAndDataExportState({
      isEditMode: false,
      state: STATES_ENTITY.NONE,
    }));
    (createDataExport(formData))(dispatch)
      .then(() => {
        (getDataExports())(dispatch);
        handleGoBack();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error:', error);
      })
      .finally(() => {
        dispatch(changeEditModeAndDataExportState({
          isEditMode: false,
          state: STATES_ENTITY.NONE,
        }));
      });
  };

  const handleEdit = () => {
    setContainerMode(MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_AND_ENTITY_DETAILS);
    setTableMode(COMPRESSED);
    dispatch(changeEditModeAndDataExportState({
      isEditMode: true,
      state: STATES_ENTITY.EDITING,
    }));
  };

  const handleSaveEdit = (formData: DataExportModel) => {
    if (!selectedDataExportId) return;

    dispatch(changeEditModeAndDataExportState({
      isEditMode: false,
      state: STATES_ENTITY.EDITING,
    }));

    (editDataExport({
      ...formData,
      id: selectedDataExportId,
    }))(dispatch)
      .then(() => {
        (getDataExports())(dispatch);
        dispatch(changeEditModeAndDataExportState({
          isEditMode: false,
          state: STATES_ENTITY.NONE,
        }));
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error:', error);
        dispatch(changeEditModeAndDataExportState({
          isEditMode: true,
          state: STATES_ENTITY.EDITING,
        }));
      });
  };

  const handleDelete = () => {
    dispatch(changeEditModeAndDataExportState({
      isEditMode: false,
      state: STATES_ENTITY.NONE,
    }));

    if (!selectedDataExportId) return;

    (deleteDataExport(selectedDataExportId))(dispatch)
      .then(() => {
        handleGoBack();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error deleting data export:', error);
      });
  };

  const handleCancelEditing = () => {
    if (originalDataExport) {
      const clonedData = _cloneDeep(originalDataExport);
      setSelectedDataExport(clonedData);
      setSelectedDataExportId(clonedData.id || null);
    }
    dispatch(changeEditModeAndDataExportState({
      isEditMode: false,
      state: STATES_ENTITY.EDITING_CANCELED,
    }));
  };

  const showDataExportCreating = () => {
    setContainerMode(MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_AND_CREATING_FORM);
    setTableMode(COMPRESSED);
  };

  const showDataExportDetails = (index: number) => {
    const selectedExport = dataExports[index];
    setOriginalDataExport(_cloneDeep(selectedExport));
    setSelectedDataExportId(selectedExport?.id || null);
    setSelectedDataExport(selectedExport);
    setSelectedIndex(index);
    setContainerMode(MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_AND_ENTITY_DETAILS);
    setTableMode(COMPRESSED);
  };

  const getForm = (): React.ReactElement | null => {
    switch (containerMode) {
      case MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_AND_CREATING_FORM:
        return (
          <DataExportCreating
            handleClickButtonCancel={handleGoBack}
            handleClickButtonSave={handleCreateNew}
          />
        );
      case MODES_SHOWING_CONTAINER_WITH_LIST_AND_FORM.SHOW_LIST_AND_ENTITY_DETAILS:
        return selectedDataExport ? (
          <DataExportDetails
            key={selectedDataExport.id}
            isEditMode={isEditMode}
            handleClickButtonCancel={isEditMode ? handleCancelEditing : handleGoBack}
            handleClickButtonBack={handleGoBack}
            handleClickButtonEdit={handleEdit}
            handleClickButtonDelete={handleDelete}
            handleClickButtonSave={handleSaveEdit}
            dataExport={selectedDataExport}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <ContainerWithListAndForm
      list={(
        <DataExportTable
          handleClickButtonNew={showDataExportCreating}
          handleClickRow={showDataExportDetails}
          isEditMode={false}
          mode={tableMode}
          selectedDataExportIndex={selectedIndex ?? -1}
          tableData={dataExports}
        />
      )}
      form={getForm()}
    />
  );
}

export default withRouter(DataExport);
